import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { formatFilingDate } from '../../utils/upcomingIpos';
import { formatCrores, formatMultiple, formatShareCount, splitAsOn, totalTone } from '../../utils/ipoSubscription';
import { formatDayShort } from '../../utils/ipoCalendar';

const TOTAL_TONES = {
  blockbuster: 'bg-[#006666] text-white border-[#006666]',
  over: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  under: 'bg-rose-50 text-rose-600 border-rose-200',
};

export const CATEGORY_COLUMNS = [
  { key: 'qib', label: 'QIB (x)' },
  { key: 'snii', label: 'sNII (x)' },
  { key: 'bnii', label: 'bNII (x)' },
  { key: 'nii', label: 'NII (x)' },
  { key: 'retail', label: 'Retail (x)', shaded: true },
  { key: 'emp', label: 'Emp (x)' },
];

// Under 1x is undersubscribed; missing categories show a dash
const multipleTone = (value) => {
  if (value === null || value === undefined) return 'text-slate-300';
  return value < 1 ? 'text-rose-500' : 'text-slate-700';
};

const StatusMarker = ({ issue }) => {
  if (issue.status === 'closing_today') {
    return (
      <span title="Bidding closes today at 5:00 PM IST" className="px-1.5 py-px rounded border border-amber-300 bg-amber-50 text-amber-700 text-[9px] font-extrabold">
        CT
      </span>
    );
  }
  if (issue.status === 'open') {
    return <span title="Bidding open" aria-label="Bidding open" className="w-2 h-2 rounded-full bg-emerald-500" />;
  }
  if (issue.status === 'upcoming') {
    return (
      <span className="px-1.5 py-px rounded border border-sky-200 bg-sky-50 text-sky-700 text-[9px] font-bold whitespace-nowrap">
        {issue.openOn ? `Opens ${formatDayShort(issue.openOn)}` : 'Upcoming'}
      </span>
    );
  }
  if (issue.status === 'listed') {
    return (
      <span className="px-1.5 py-px rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-[9px] font-bold whitespace-nowrap">
        Listed {issue.listingGain >= 0 ? '+' : '−'}
        {Math.abs(issue.listingGain)}%
      </span>
    );
  }
  return <span className="px-1.5 py-px rounded border border-slate-200 bg-slate-100 text-slate-500 text-[9px] font-bold">Closed</span>;
};

const PageButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

export const LiveBiddingTable = ({
  issues,
  page,
  pageSize,
  onPageChange,
  emptyMessage,
  title = 'Live Bidding Multiples (Exchange Consolidated)',
  badge = 'BSE & NSE',
  columns = CATEGORY_COLUMNS,
}) => {
  // Company, closing date, offer amount, total, shares bid and as-on, plus the category columns
  const columnCount = 6 + columns.length;
  const totalPages = Math.max(1, Math.ceil(issues.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = issues.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstIndex = issues.length ? (currentPage - 1) * pageSize + 1 : 0;
  const lastIndex = Math.min(currentPage * pageSize, issues.length);

  return (
    <section aria-labelledby="live-bidding-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-200/80">
        <div className="flex items-center gap-2">
          <h2 id="live-bidding-heading" className="text-sm sm:text-base font-extrabold text-slate-900">
            {title}
          </h2>
          <span className="px-2 py-0.5 rounded-md border border-teal-200 bg-teal-50 text-[10px] font-bold text-[#006666]">{badge}</span>
        </div>
        <ul className="flex items-center gap-4 text-[11px] text-slate-500">
          <li className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm border border-emerald-300 bg-emerald-100" />
            Oversubscribed (&gt;1.0x)
          </li>
          <li className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm border border-rose-200 bg-rose-100" />
            Undersubscribed (&lt;1.0x)
          </li>
        </ul>
      </div>

      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <th className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[220px]">Company Name</th>
              <th className="py-3 px-3 whitespace-nowrap">Closing Date</th>
              <th
                className="py-3 px-3 text-right whitespace-nowrap"
                title="Shares offered for bidding (excludes the anchor portion) at the upper price band"
              >
                Offer Amt (₹ Cr)
              </th>
              {columns.map((col) => (
                <th key={col.key} className={`py-3 px-3 text-right whitespace-nowrap ${col.shaded ? 'bg-teal-50 text-[#006666]' : ''}`}>
                  {col.label}
                </th>
              ))}
              <th className="py-3 px-3 text-right whitespace-nowrap">Total (x)</th>
              <th className="py-3 px-3 text-right whitespace-nowrap">Shares Bid</th>
              <th className="py-3 pl-3 pr-4 sm:pr-5 whitespace-nowrap">Subscription As On</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="py-12 text-center text-sm text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {pageRows.map((issue) => {
              const asOn = issue.asOn ? splitAsOn(issue.asOn) : null;
              return (
                <tr key={issue.id} className="group hover:bg-slate-50/70 transition-colors">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 py-3 pl-4 sm:pl-5 pr-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {issue.symbol ? (
                        <Link to={`/ipo-subscription/${encodeURIComponent(issue.symbol)}`} className="font-bold text-slate-900 hover:text-[#008080]">
                          {issue.company}
                        </Link>
                      ) : (
                        <span className="font-bold text-slate-900">{issue.company}</span>
                      )}
                      <StatusMarker issue={issue} />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {issue.detail ?? issue.sector}
                      {issue.error && <span className="text-amber-600"> · NSE data unavailable</span>}
                    </div>
                  </td>
                  <td className="py-3 px-3 whitespace-nowrap text-slate-600">{formatFilingDate(issue.closeOn) || '-'}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                    {typeof issue.issueCr === 'number' ? formatCrores(issue.issueCr) : <span className="text-slate-300">-</span>}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`py-3 px-3 text-right whitespace-nowrap tabular-nums font-medium ${multipleTone(issue[col.key])} ${
                        col.shaded ? 'bg-teal-50/40 font-bold' : ''
                      }`}
                    >
                      {formatMultiple(issue[col.key])}
                    </td>
                  ))}
                  <td className="py-3 px-3 text-right whitespace-nowrap">
                    {typeof issue.total === 'number' ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-extrabold tabular-nums ${TOTAL_TONES[totalTone(issue.total)]}`}>
                        {formatMultiple(issue.total)}x
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                    {issue.sharesBid ? (
                      <>
                        <div>{formatShareCount(issue.sharesBid)}</div>
                        {issue.sharesOffered ? (
                          <div className="text-[10px] text-slate-400">of {formatShareCount(issue.sharesOffered)}</div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="py-3 pl-3 pr-4 sm:pr-5 whitespace-nowrap text-[11px] text-slate-500 leading-tight">
                    {asOn ? (
                      <>
                        <div>{asOn.date}</div>
                        <div>{asOn.time} IST</div>
                      </>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
        <p className="text-xs text-slate-500">
          Showing {firstIndex} to {lastIndex} of {issues.length} IPOs
        </p>
        {totalPages > 1 && (
          <nav className="flex items-center gap-1" aria-label="Subscription table pagination">
            <PageButton onClick={() => onPageChange(1)} disabled={currentPage <= 1} label="First page">
              <ChevronsLeft className="w-3.5 h-3.5" />
              First
            </PageButton>
            <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} label="Previous page">
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </PageButton>
            {buildPageList(currentPage, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                  className={`w-8 h-8 rounded-lg text-xs font-bold ${
                    p === currentPage ? 'bg-[#008080] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <PageButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} label="Next page">
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </PageButton>
            <PageButton onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} label="Last page">
              Last
              <ChevronsRight className="w-3.5 h-3.5" />
            </PageButton>
          </nav>
        )}
      </div>
    </section>
  );
};

export default LiveBiddingTable;
